import { useEffect, useMemo, useRef, useState } from 'react'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import QrCodeScannerRoundedIcon from '@mui/icons-material/QrCodeScannerRounded'
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded'
import VolunteerActivismRoundedIcon from '@mui/icons-material/VolunteerActivismRounded'
import { useNls } from '../../nls/nlsProvider'
import {
  subscribeHelperVouchEvents,
  subscribeReliabilityStats,
  type ReliabilitySnapshot,
  type ReliabilityStats,
  type WorkspaceSocialEvent,
} from '../../services/reliabilityService'
import { subscribeOpenHelperBlockers } from '../../services/helper/helperBlockerService'
import type { HelperAssignedItem } from '../../services/helper/helperAssignedItemsService'
import { HELPER_BLOCKER_REASON_LABELS, type HelperOpenBlocker } from '../../types/helperBlocker'
import { IconButton } from '../Common'
import { HelperEmptyStateSlim } from './HelperEmptyState'
import { computeHelperMeInsight } from '../../services/helper/helperMeInsights'

export const emptyReliabilitySnapshot = (uid: string, workspaceId: string): ReliabilitySnapshot => ({
  uid,
  workspaceId,
  reliabilityScore: null,
  reliabilityStats: null,
  updatedAt: null,
})

export const reliabilityCompletedWork = (stats: ReliabilityStats | null | undefined): number =>
  (stats?.completedChecklistItems ?? 0) + (stats?.completedTasks ?? 0)

export const resolveReliabilityVouchCount = (
  stats: ReliabilityStats | null | undefined,
  events: readonly WorkspaceSocialEvent[],
): number => stats?.vouches ?? events.length

export const filterHelperVouchEvents = (
  events: readonly WorkspaceSocialEvent[],
  uid: string,
): WorkspaceSocialEvent[] => events.filter((event) =>
  event.type === 'VOUCH_CREATED' && Array.isArray(event.participantUids) && event.participantUids.includes(uid),
)

const statLabelClass = 'text-[10px] font-black uppercase tracking-[0.12em] text-[var(--muted)]'

const BlockerRows = ({ blockers }: { blockers: HelperOpenBlocker[] }) => {
  const { t } = useNls()
  if (blockers.length === 0) {
    return <HelperEmptyStateSlim title={t('NLSHelperReliabilityNoBlockers')} className="mt-2 px-0 py-0" />
  }
  return (
    <div className="mt-2 grid gap-2">
      {blockers.slice(0, 5).map((blocker) => (
        <div key={blocker.id} className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-3">
          <div className="flex items-center justify-between gap-3">
            <strong className="min-w-0 truncate text-xs font-black text-[var(--text-strong)]">{blocker.itemTitle}</strong>
            <span className="shrink-0 rounded-full bg-[color-mix(in_srgb,var(--warning)_10%,transparent)] px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.08em] text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))]">
              {HELPER_BLOCKER_REASON_LABELS[blocker.reason]}
            </span>
          </div>
          {blocker.note ? <p className="mt-1 line-clamp-2 text-[11px] font-semibold leading-relaxed text-[var(--muted)]">{blocker.note}</p> : null}
        </div>
      ))}
    </div>
  )
}

export const HelperReliabilityPanel = ({
  workspaceId,
  actor,
  items,
}: {
  workspaceId: string
  actor: { id: string; name: string } | null
  items: HelperAssignedItem[]
}) => {
  const { t } = useNls()
  const uid = actor?.id ?? ''
  const [snapshot, setSnapshot] = useState<ReliabilitySnapshot>(() => emptyReliabilitySnapshot(uid, workspaceId))
  const [events, setEvents] = useState<WorkspaceSocialEvent[]>([])
  const [blockers, setBlockers] = useState<HelperOpenBlocker[]>([])
  const [loading, setLoading] = useState(true)
  const [openTile, setOpenTile] = useState<'SCANS' | 'DONE' | 'VOUCHES' | 'BLOCKERS' | null>(null)
  const tilesRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setLoading(true)
    if (!workspaceId || !uid) {
      setSnapshot(emptyReliabilitySnapshot(uid, workspaceId))
      setLoading(false)
      return
    }
    return subscribeReliabilityStats(
      workspaceId,
      uid,
      (next) => {
        setSnapshot(next)
        setLoading(false)
      },
      () => {
        setSnapshot(emptyReliabilitySnapshot(uid, workspaceId))
        setLoading(false)
      },
    )
  }, [uid, workspaceId])

  useEffect(() => {
    if (!workspaceId || !uid) {
      setEvents([])
      return
    }
    return subscribeHelperVouchEvents(workspaceId, uid, setEvents, () => setEvents([]))
  }, [uid, workspaceId])

  const itemIds = useMemo(() => items.map((item) => item.id), [items])
  useEffect(() => {
    if (!workspaceId || !uid || itemIds.length === 0) {
      setBlockers([])
      return
    }
    return subscribeOpenHelperBlockers(workspaceId, uid, itemIds, setBlockers, () => setBlockers([]))
  }, [itemIds, uid, workspaceId])

  const vouchEvents = useMemo(() => filterHelperVouchEvents(events, uid), [events, uid])
  const stats = snapshot.reliabilityStats
  const score = snapshot.reliabilityScore
  const vouches = resolveReliabilityVouchCount(stats, vouchEvents)
  const completedWork = reliabilityCompletedWork(stats)
  useEffect(() => {
    if (!openTile) return undefined
    const close = (event: MouseEvent) => { if (!tilesRef.current?.contains(event.target as Node)) setOpenTile(null) }
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpenTile(null) }
    document.addEventListener('mousedown', close); document.addEventListener('keydown', escape)
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', escape) }
  }, [openTile])
  const insight = computeHelperMeInsight({ card: 'RELIABILITY', reliability: { completedCount: completedWork, vouchCount: vouches } })
  const tiles = [
    ['SCANS', QrCodeScannerRoundedIcon, t('NLSHelperReliabilityScans'), stats?.successfulScans ?? 0, t('NLSHelperMeReliabilityScansHint')],
    ['DONE', TaskAltRoundedIcon, t('NLSHelperReliabilityDone'), completedWork, t('NLSHelperMeReliabilityDoneHint')],
    ['VOUCHES', VolunteerActivismRoundedIcon, t('NLSHelperReliabilityVouches'), vouches, t('NLSHelperMeReliabilityVouchesHint')],
    ['BLOCKERS', ReportProblemRoundedIcon, t('NLSHelperReliabilityBlockers'), stats?.blockerEvents ?? blockers.length, t('NLSHelperMeReliabilityBlockersHint')],
  ] as const

  return (
    <section className="relative overflow-hidden rounded-3xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-[var(--accent-strong)]">
            <VerifiedRoundedIcon style={{ fontSize: 14 }} /> {t('NLSHelperReliabilityEyebrow')}
          </p>
          <p className="mt-1 text-3xl font-semibold leading-none text-[var(--text-strong)]">{!loading && completedWork > 0 ? completedWork : '—'}</p>
        </div>
      </div>
      <p className="mt-3 text-xs font-medium leading-relaxed text-[var(--muted)]">{insight}</p>

      <div ref={tilesRef} className="mt-4 grid grid-cols-2 gap-2">
        {tiles.map(([key, Icon, label, value, hint]) => <div key={key} className="relative rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-3"><span className="inline-flex items-center gap-1 text-[var(--text-strong)]"><Icon style={{ fontSize: 14 }} /><span className={statLabelClass}>{label}</span><IconButton variant="tertiary" size="md" label={`${label} info`} icon={<InfoOutlinedIcon style={{ fontSize: 12 }} />} onClick={() => setOpenTile(key)} /></span><strong className="mt-1 block text-xl font-black text-[var(--text-strong)]">{value}</strong>{openTile === key ? <span className="absolute right-2 top-7 z-10 w-52 rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-3 text-[10px] font-semibold text-[var(--muted)] shadow-[var(--shadow-pop)]">{hint}</span> : null}</div>)}
      </div>

      <div className="mt-3 rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-3">
        <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[var(--accent-strong)]">{t('NLSHelperReliabilityStreak')}</p>
        <p className="mt-1 text-sm font-semibold text-[var(--text-strong)]">
          {score === null ? t('NLSHelperMeReliabilityKicksInAfter', { count: completedWork }) : t('NLSHelperReliabilityStreakBody', { count: completedWork, days: stats?.windowDays ?? 30 })}
        </p>
        {score === null ? <div className="mt-2 h-1 rounded-full bg-[var(--border-subtle)]"><div className="h-full rounded-full bg-[var(--accent)] transition-[width] duration-500 ease-out" style={{ width: `${Math.min(100, (completedWork / 5) * 100)}%` }} /></div> : null}
      </div>

      <div className="mt-4">
        <p className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))]">
          <ReportProblemRoundedIcon style={{ fontSize: 14 }} /> {t('NLSHelperReliabilityRecentBlockers')}
        </p>
        <BlockerRows blockers={blockers} />
      </div>
    </section>
  )
}
